import { createApi } from "@reduxjs/toolkit/query/react";
import {
  listTrapSitesResponse,
  createTrapSiteRequest,
  createTrapSiteResponse,
  updateTrapSiteRequest,
  trapSiteWriteResponse,
  geocodeSearchResponse,
  geocodeReverseResponse,
  geocodeSuggestResponse,
  GPS,
} from "../../types";
import { axiosBaseQuery } from "../../providers/auth0-instance";
import {
  withErrorNotification,
  displayErrorNotification,
} from "../notifications";
// Imported by file rather than through ../ - the actions barrel re-exports both
// slices, and going through it invites a cycle. alertsInQuery reaches for
// alertCountsApi the same way, for the same reason.
import { deviceApi } from "../devicesInQuery";

/**
 * Writing a trap site can move devices between locations, and `trap_site_id`
 * lives on the device - but deviceApi is a separate createApi instance, so a
 * "Device" tag from this slice would invalidate nothing. Refreshing it by hand
 * is also what refreshes TrapLocations, Traps, AlertTable, catalogItemsSlice
 * and useAppAlerts, all of which derive from the device list.
 */
const withDeviceRefresh =
  (message: string) =>
  async (_arg: unknown, { dispatch, queryFulfilled }: any) => {
    try {
      await queryFulfilled;
      dispatch(deviceApi.util.invalidateTags(["Device"]));
    } catch (err) {
      dispatch(displayErrorNotification(message));
    }
  };

export const trapSiteApi = createApi({
  reducerPath: "trapSiteApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["TrapSite"],
  endpoints: (builder) => ({
    listTrapSites: builder.query<
      listTrapSitesResponse,
      { org?: string } | void
    >({
      query: (args) => ({ url: "/trap-sites", org: args?.org }),
      providesTags: ["TrapSite"],
      onQueryStarted: withErrorNotification("Failed to list trap sites"),
    }),
    createTrapSite: builder.mutation<
      createTrapSiteResponse,
      createTrapSiteRequest
    >({
      query: (data) => ({ url: "/trap-site", method: "POST", data }),
      invalidatesTags: ["TrapSite"],
      onQueryStarted: withDeviceRefresh("Failed to create collection location"),
    }),
    updateTrapSite: builder.mutation<
      trapSiteWriteResponse,
      { id: string } & updateTrapSiteRequest
    >({
      query: ({ id, ...data }) => ({
        url: `/trap-site/${id}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: ["TrapSite"],
      onQueryStarted: withDeviceRefresh("Failed to save collection location"),
    }),
    deleteTrapSite: builder.mutation<trapSiteWriteResponse, string>({
      query: (id) => ({ url: `/trap-site/${id}`, method: "DELETE" }),
      invalidatesTags: ["TrapSite"],
      onQueryStarted: withDeviceRefresh("Failed to delete collection location"),
    }),

    // Geocoding is billable per request, so both of these are used through
    // their lazy hooks - a non-lazy useQuery would fire on mount, on cache-key
    // churn and on every remount. keepUnusedDataFor keeps a re-opened panel or
    // a retried lookup off the network entirely.
    geocodeSearch: builder.query<
      geocodeSearchResponse,
      { q: string; limit?: number }
    >({
      query: ({ q, limit }) => ({
        url: "/geocode/search",
        params: { q, ...(limit ? { limit } : {}) },
      }),
      keepUnusedDataFor: 300,
      onQueryStarted: withErrorNotification("Failed to look up that address"),
    }),
    // Cheap enough to call per keystroke (debounced). Its results are
    // suggestions only - see geocodeSuggestResponse.
    geocodeSuggest: builder.query<
      geocodeSuggestResponse,
      { q: string; near?: GPS | null }
    >({
      // `near` biases suggestions toward the pin being edited. The API refuses
      // a suggest with no bias at all, so the backend falls back to the org's
      // map centre when this is absent.
      query: ({ q, near }) => ({
        url: "/geocode/suggest",
        params: {
          q,
          ...(near
            ? { lat: near.lat.toFixed(6), lon: near.lon.toFixed(6) }
            : {}),
        },
      }),
      keepUnusedDataFor: 300,
      // No error toast: a failed suggestion while typing is not worth
      // interrupting anyone over, and Find reports properly.
    }),
    geocodeReverse: builder.query<geocodeReverseResponse, GPS>({
      // Rounded before it becomes a cache key: dragging a pin otherwise mints
      // an unbounded number of entries for what is effectively one place.
      query: ({ lat, lon }) => ({
        url: "/geocode/reverse",
        params: { lat: lat.toFixed(6), lon: lon.toFixed(6) },
      }),
      keepUnusedDataFor: 300,
      onQueryStarted: withErrorNotification(
        "Failed to look up the address for that point",
      ),
    }),
  }),
});

export const {
  useListTrapSitesQuery,
  useCreateTrapSiteMutation,
  useUpdateTrapSiteMutation,
  useDeleteTrapSiteMutation,
  useLazyGeocodeSearchQuery,
  useLazyGeocodeReverseQuery,
  useLazyGeocodeSuggestQuery,
} = trapSiteApi;
